import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, FileText, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const LeasePlansWestCountry = () => {
  const faqs = [
    {
      question: "How much do lease plans cost across the West Country?",
      answer: "Lease plan pricing varies based on property complexity and demise structure. Simple single-unit leases (small shops, offices) typically cost £150-£250. Multi-let buildings with shared areas and complex demises range from £300-£600+. Large commercial premises or industrial estates may cost £600-£1500+ depending on scope. Contact us with your property details for an accurate quote."
    },
    {
      question: "Do you cover the entire West Country for lease plans?",
      answer: "Yes, we provide professional lease plan services across Bristol, Devon, Somerset, Dorset, and Cornwall. From Bristol city centre to Plymouth, Taunton to Dorchester, and Exeter to Truro, we cover all major West Country commercial property markets. Travel surcharge may apply to more remote locations."
    },
    {
      question: "Are your lease plans Land Registry compliant?",
      answer: "Yes, all our lease plans are drawn to Land Registry standards and are suitable for commercial lease agreements, property sales, land registration, and legal documentation. Plans clearly show demised areas, shared spaces, common parts, and access routes as required for formal lease documentation."
    },
    {
      question: "How long does it take to produce lease plans?",
      answer: "The on-site measurement typically takes 1-3 hours depending on building size and complexity. Once measured, lease plans are delivered within 5-7 working days in CAD format (DWG, DXF) and PDF. Expedited turnaround available for urgent West Country commercial transactions."
    },
    {
      question: "What information do lease plans include?",
      answer: "Our West Country lease plans include demised areas (tenant-exclusive spaces), common parts (shared corridors, stairs, toilets), access routes, service areas, external boundaries, parking allocations where relevant, and clear labeling of all zones. Plans are drawn to scale with accurate dimensions and total area calculations."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Lease Plans West Country | Commercial Lease Plans | Bristol, Devon, Somerset, Dorset, Cornwall</title>
        <meta name="description" content="Professional lease plans across the West Country from £150. Land Registry compliant. Covering Bristol, Devon, Somerset, Dorset, Cornwall. Fast delivery. Call 01392 980077." />
        <meta name="keywords" content="lease plans west country, commercial lease plans Bristol, lease plans Devon, lease plans Cornwall, Land Registry lease plans, multi-let lease plans, demise plans" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/lease-plans-west-country" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Bristol to Cornwall Coverage</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Commercial Lease Plans Across the West Country
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Professional lease plans for commercial properties throughout Bristol, Devon, Somerset, Dorset and Cornwall. Land Registry compliant, multi-let buildings, demise plans.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking">
                  <Button className="text-white hover:opacity-90 px-8 py-6 text-lg rounded-full shadow-lg" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                    Book Lease Plan Service
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Land Registry Compliant</h3>
                  <p className="text-sm text-gray-600">Professional standards</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Clock className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">5-7 Day Delivery</h3>
                  <p className="text-sm text-gray-600">Fast turnaround</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Regional Coverage</h3>
                  <p className="text-sm text-gray-600">Bristol to Cornwall</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <FileText className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">From £150</h3>
                  <p className="text-sm text-gray-600">Competitive pricing</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Lease Plans for West Country Commercial Properties</h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Energy Performance Direct provides professional lease plans for commercial properties throughout the West Country. Operating since 2009, we have extensive experience preparing Land Registry-compliant lease plans for multi-let buildings, retail parades, office blocks, industrial estates, and mixed-use developments across Bristol, Devon, Somerset, Dorset, and Cornwall.
                </p>

                <p>
                  A lease plan is a scaled drawing showing the extent of a leasehold property (the demise), common parts, shared facilities, and access routes. Lease plans are required for commercial property lettings to clearly define what the tenant is leasing, what areas are shared, and what rights of access exist. Accurate, professionally drawn lease plans prevent disputes, clarify responsibilities for maintenance and service charges, and are essential for Land Registry submissions and formal lease agreements.
                </p>

                <p>
                  Our West Country lease plan service covers all property types including retail units in high street locations, office suites in business parks, industrial units in commercial estates, restaurants and pubs with outdoor areas, and complex multi-let buildings with shared services. We work closely with commercial property solicitors, surveyors, and managing agents across the region to ensure plans meet legal and practical requirements.
                </p>

                <p>
                  All lease plans are drawn using professional CAD software and delivered in DWG, DXF and PDF formats suitable for solicitors, Land Registry submissions, and property documentation. Plans clearly show demised areas with dimensions, common parts (lobbies, stairs, corridors, toilets), service areas (plant rooms, bin stores), external boundaries, parking allocations, and access rights.
                </p>

                <p>
                  Lease plan pricing across the West Country: Simple single-unit leases £150-£250, Multi-let buildings £300-£600+, Large estates or complex demises £600-£1500+. Travel surcharge may apply to more remote Cornwall locations. Contact us for accurate quotes.
                </p>
              </div>

              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">FAQs - West Country Lease Plans</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="mt-16 rounded-2xl p-8 text-white text-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                <h2 className="text-3xl font-bold mb-4">Book Your West Country Lease Plan</h2>
                <p className="text-lg mb-6">Professional lease plans across Bristol, Devon, Somerset, Dorset, Cornwall. Call 01392 980077.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">Book Online Now</Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default LeasePlansWestCountry;